'use client';

import { useState, useEffect } from 'react';
// import "bootstrap/dist/css/bootstrap.min.css"

const ChatHistoryModal = ({ isOpen, onClose, sessionId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch conversation history when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      fetchConversationHistory();
    }
  }, [isOpen, sessionId]);

  const fetchConversationHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      // Replace with your actual API endpoint
      const response = await fetch(`/api/conversations/${sessionId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch conversation history');
      }

      const responseData = await response.json();
      console.log('responseData', responseData.data.messages);
      setMessages(responseData.data.messages || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching conversation:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = timestamp => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';

    return (
      <div
        key={index}
        className={`d-flex mb-3 ${isUser ? 'justify-content-end' : 'justify-content-start'}`}
      >
        <div
          className={`message-bubble ${isUser ? 'user-message' : isSystem ? 'system-message' : 'assistant-message'}`}
        >
          <div className="message-header d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted fw-bold">
              {isUser ? 'User' : isSystem ? 'System' : 'Assistant'}
            </small>
            <small className="text-muted">{formatTimestamp(message.timestamp)}</small>
          </div>
          <div className="message-content">{message.content}</div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Bootstrap Modal Backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
      ></div>

      {/* Bootstrap Modal */}
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-header bg-gradient text-dark">
              <h5 className="modal-title">
                <i className="bi bi-chat-dots me-2"></i>
                OpenAI Conversation History
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            {/* Session Info */}
            <div className="modal-body p-0">
              <div className="bg-white border-bottom p-3 shadow-sm">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Session ID:</strong>
                    <span className="ms-2 badge bg-light text-dark border">{sessionId}</span>
                  </div>
                  {/* <div>
                    <strong>Messages:</strong>
                    <span className="ms-2 badge bg-light text-primary border">{messages.length}</span>
                  </div> */}
                </div>
              </div>

              {/* Chat Messages Container */}
              <div className="chat-container p-3" style={{ overflowY: 'auto' }}>
                {loading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading conversation history...</p>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                {!loading && !error && messages.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-chat-square-text fs-1 mb-3 d-block"></i>
                    <p>No messages found for this session.</p>
                  </div>
                )}

                {!loading && !error && messages.length > 0 && (
                  <div className="messages-list">
                    {messages.map((message, index) => renderMessage(message, index))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer bg-white border-top">
              <button type="button" className="btn btn-light border" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={fetchConversationHistory}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .message-bubble {
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 18px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .user-message {
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          color: #1565c0;
          border: 1px solid #e1f5fe;
          border-bottom-right-radius: 4px;
        }

        .assistant-message {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          color: #495057;
          border: 1px solid #e9ecef;
          border-bottom-left-radius: 4px;
        }

        .system-message {
          background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
          color: #f57f17;
          border: 1px solid #ffe0b2;
          border-radius: 8px;
          font-style: italic;
          margin: 0 auto;
          max-width: 80%;
        }

        .message-content {
          line-height: 1.5;
          word-wrap: break-word;
        }

        .user-message .message-header small {
          color: #1976d2;
          opacity: 0.8;
        }

        .chat-container {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
        }

        .modal-backdrop {
          background-color: rgba(0, 0, 0, 0.2);
        }

        .bg-gradient {
          background: linear-gradient(135deg, #ffffff 0%, #f1f3f4 100%);
          border-bottom: 1px solid #e9ecef;
        }

        .messages-list {
          padding-bottom: 20px;
        }

        @media (max-width: 768px) {
          .message-bubble {
            max-width: 85%;
          }

          .modal-dialog {
            margin: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default ChatHistoryModal;
